import { useTranslation } from "react-i18next";

export type SerialIssue = "duplicateSerial" | "wrongSerial" | "invalidSerial";
export const serialLines = (text: string) =>
  text.split("\n").filter((line) => !/^ *$/.test(line));
export const serialIdentity = (text: string) =>
  text.replace(/^ +| +$/g, "").toUpperCase();

export function serialProblem(
  serials: string[],
  box: string,
  ean?: string | null,
): SerialIssue | null {
  // Match _serial_values: reject raw controls before space-only display trim.
  if (
    serials.length > 10000 ||
    serials.some((raw) => {
      if (raw.length < 1 || raw.length > 162 || /[^\x20-\x7e]/.test(raw))
        return true;
      const display = raw.replace(/^ +| +$/g, "");
      return display.length < 1 || display.length > 160;
    })
  )
    return "invalidSerial";
  if (new Set(serials.map(serialIdentity)).size !== serials.length)
    return "duplicateSerial";
  if (
    serials.some(
      (s) =>
        serialIdentity(s).startsWith("BX-") ||
        serialIdentity(s) === serialIdentity(box) ||
        (ean && serialIdentity(s) === ean.toUpperCase()),
    )
  )
    return "wrongSerial";
  return null;
}

export function validBoxCode(value: string): boolean {
  // Exact opaque format from validate_box_codes; no trim or case repair.
  const match = /^BX-[A-Z0-9-]{1,32}$/.exec(value);
  return value.length <= 40 && match?.[0] === value;
}

const en = {
  invalidSerial:
    "UNIT: use 1–160 printable ASCII characters (raw input at most 162). Controls are not allowed.",
  invalidBox:
    "BOX: use an unchanged BX- identifier with 1–32 uppercase letters, digits or hyphens after the prefix.",
};
const nl: typeof en = {
  invalidSerial:
    "UNIT: gebruik 1–160 afdrukbare ASCII-tekens (ruwe invoer maximaal 162). Besturingstekens zijn niet toegestaan.",
  invalidBox:
    "BOX: gebruik een ongewijzigde BX-code met 1–32 hoofdletters, cijfers of koppeltekens na het voorvoegsel.",
};
const fr: typeof en = {
  invalidSerial:
    "UNIT : utilisez 1–160 caractères ASCII imprimables (saisie brute de 162 maximum). Les caractères de contrôle sont interdits.",
  invalidBox:
    "BOX : utilisez un identifiant BX- inchangé avec 1–32 majuscules, chiffres ou traits d’union après le préfixe.",
};
export function useIntakeValidationText(): typeof en {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const texts = { en, nl, fr };
  return texts[lang as keyof typeof texts] || en;
}
